import { useState, useEffect, useCallback } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Search, Filter, Download, RefreshCw } from 'lucide-react';
import { auditService, AuditLogDto, AuditLogRequest } from '@/services/auditService';
import { formatLocalDateTime } from '@/lib/date';
import { toast } from '@/components/ui/sonner';

const AuditLogs = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLogDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterEventType, setFilterEventType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch audit logs
  const fetchAuditLogs = useCallback(async (filters: Partial<AuditLogRequest> = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const request: AuditLogRequest = {
        page: currentPage,
        pageSize: 50,
        eventType: filterEventType !== 'all' ? filterEventType : undefined,
        userId: searchTerm || undefined,
        ...filters
      };

      const response = await auditService.getAuditLogs(request);
      setAuditLogs(response.logs);
      setTotalCount(response.totalCount);
      setTotalPages(response.totalPages);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch audit logs';
      setError(errorMessage);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterEventType, searchTerm]);

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  // Handle search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchAuditLogs();
      } else {
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, currentPage, fetchAuditLogs]);

  // Filter logs locally by status since the API doesn't support status filtering
  const filteredLogs = auditLogs.filter(log => {
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'success' && log.success) ||
      (filterStatus === 'failed' && !log.success);
    
    return matchesStatus;
  });

  const getStatusBadge = (success: boolean) => {
    return success ? 
      <Badge className="bg-green-100 text-green-800">Success</Badge> :
      <Badge variant="destructive">Failed</Badge>;
  };

  const getEventTypeBadge = (eventType: string) => {
    const colorMap: Record<string, string> = {
      MemoryWrite: 'bg-blue-100 text-blue-800',
      MemoryRecall: 'bg-green-100 text-green-800',
      MemoryExport: 'bg-yellow-100 text-yellow-800',
      MemoryDelete: 'bg-red-100 text-red-800',
      ConsentDenied: 'bg-purple-100 text-purple-800'
    };
    
    return (
      <Badge className={colorMap[eventType] || 'bg-gray-100 text-gray-800'}>
        {eventType.replace(/([A-Z])/g, ' $1').trim()}
      </Badge>
    );
  };

  const formatTimestamp = (timestamp: string) => formatLocalDateTime(timestamp);

  const formatMetadata = (metadata: string) => {
    try {
      const parsed = JSON.parse(metadata);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return metadata;
    }
  };

  const handleRefresh = () => {
    fetchAuditLogs();
  };

  const handleExport = async () => {
    try {
      const exportRequest: AuditLogRequest = {
        eventType: filterEventType !== 'all' ? filterEventType : undefined,
        userId: searchTerm || undefined,
      };

      await auditService.exportAuditLogs(exportRequest);
      toast.success('Audit logs exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export logs');
    }
  };

  // Show loading state
  if (loading && auditLogs.length === 0) {
    return (
      <PageLayout 
        title="Audit Logs" 
        description="Complete audit trail of all memory operations for compliance tracking"
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading audit logs...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Show error state
  if (error && auditLogs.length === 0) {
    return (
      <PageLayout 
        title="Audit Logs" 
        description="Complete audit trail of all memory operations for compliance tracking"
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-500 mb-4">Error: {error}</p>
            <Button onClick={handleRefresh} variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title="Audit Logs" 
      description="Complete audit trail of all memory operations for compliance tracking"
    >
      <div className="space-y-6">
        {/* Filters */}
        <Card className="p-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by user ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={filterEventType} onValueChange={setFilterEventType}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Event Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="MemoryWrite">Write</SelectItem>
                <SelectItem value="MemoryRecall">Recall</SelectItem>
                <SelectItem value="MemoryExport">Export</SelectItem>
                <SelectItem value="MemoryDelete">Delete</SelectItem>
                <SelectItem value="ConsentDenied">Consent Denied</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={handleRefresh} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            <Button variant="outline" onClick={handleExport} className="flex items-center gap-2">
              <Download size={16} />
              Export Logs
            </Button>
          </div>
        </Card>

        {/* Results count and pagination info */}
        <div className="flex justify-between items-center text-sm text-muted-foreground">
          <div>
            Showing {filteredLogs.length} of {totalCount} audit entries
            {currentPage > 1 && ` (Page ${currentPage} of ${totalPages})`}
          </div>
          {totalPages > 1 && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1 || loading}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || loading}
              >
                Next
              </Button>
            </div>
          )}
        </div>

        {/* Audit Logs Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event Type</TableHead>
                <TableHead>Timestamp</TableHead>
                <TableHead>User ID</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Metadata</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    {loading ? 'Loading...' : 'No audit logs found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {getEventTypeBadge(log.eventType)}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatTimestamp(log.timestamp)}
                    </TableCell>
                    <TableCell className="font-mono">
                      {log.userId || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(log.success)}
                    </TableCell>
                    <TableCell>
                      <pre className="text-xs bg-muted p-2 rounded max-w-[200px] overflow-x-auto">
                        {formatMetadata(log.metadata)}
                      </pre>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </PageLayout>
  );
};

export default AuditLogs;
